import { useEffect, useState, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ChevronLeft, ChevronRight, Search } from "lucide-react";
import { getAllCategories } from "@/services/menuService";
import { Category } from "@/types";
import { Input } from "./ui/input";
interface FoodCategoriesProps {
  onCategorySelect: (category: string) => void;
  selectedCategory: string;
  highlightedCategory: string;
  searchQuery: string;
  setSearchQuery: (query: string) => void;
}

export function FoodCategories({
  onCategorySelect,
  selectedCategory,
  highlightedCategory,
  searchQuery,
  setSearchQuery,
}: FoodCategoriesProps) {
  const [scrollPosition, setScrollPosition] = useState(0);
  const [categories, setCategories] = useState<Category[]>([]);
  const categoryRefs = useRef<{ [key: string]: HTMLButtonElement | null }>({});
  const fetchCategories = async () => {
    try {
      const response = await getAllCategories();
      // Calculate total count from all categories
      const totalCount = response.reduce(
        (acc: any, cat: any) => acc + cat.count,
        0,
      );

      // Create the "All Items" category object
      const allItemsCategory = {
        categoryId: "All",
        name: "All Items",
        count: totalCount,
      };

      // Ensure all categoryIds are strings
      const normalizedCategories = response.map((cat: any) => ({
        ...cat,
        categoryId: String(cat.categoryId),
      }));

      // Insert it at the start of the array
      let categoriesWithAll = [allItemsCategory, ...normalizedCategories];
      // ✅ Sort by category name A → Z
      categoriesWithAll = categoriesWithAll?.sort((a: any, b: any) =>
        a.name?.localeCompare(b.name),
      );
      setCategories(categoriesWithAll);
    } catch (err) {
      console.log(err);
    }
  };
  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    const activeButton = categoryRefs.current[highlightedCategory];
    if (activeButton) {
      activeButton.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "center",
      });
    }
  }, [highlightedCategory]);
  const scrollCategories = (direction: "left" | "right") => {
    const container = document.getElementById("categories-scroll");
    if (container) {
      const scrollAmount = 200;
      const newPosition =
        direction === "left"
          ? Math.max(0, scrollPosition - scrollAmount)
          : Math.min(
              container.scrollWidth - container.clientWidth,
              scrollPosition + scrollAmount,
            );

      container.scrollTo({ left: newPosition, behavior: "smooth" });
      setScrollPosition(newPosition);
    }
  };

  return (
    <section className="sticky top-16 sm:top-20 z-40 py-6 sm:py-8 bg-white border-b border-gray-100 shadow-sm">
      <div className="max-w-7xl mx-auto mobile-padding">
        <div className="text-center mb-6">
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">
            Our Menu
          </h2>
          {/* <p className="text-gray-600 text-sm sm:text-base">
            Choose from our authentic Thai dishes
          </p> */}
        </div>

        {/* Desktop Categories */}
        <div className="hidden lg:flex flex-wrap justify-center gap-2">
          {categories
            .filter((category) => category.count > 0)
            // .slice(0, 12)
            .map((category, index) => (
              <Button
                key={category.categoryId}
                ref={(el) => (categoryRefs.current[category.categoryId] = el)}
                variant={
                  highlightedCategory === category.categoryId
                    ? "default"
                    : "outline"
                }
                onClick={() => onCategorySelect(category.categoryId)}
                className={`h-10 px-6 transition-all duration-200 ${
                  highlightedCategory === category.categoryId
                    ? "bg-emerald-600 hover:bg-emerald-700 text-white shadow-lg"
                    : "hover:bg-emerald-50 hover:border-emerald-300 hover:text-emerald-600 hover:shadow-md"
                }`}
              >
                {/* <span className="mr-2">{category.emoji}</span> */}
                <span className="font-medium">{category.name}</span>
                {/* <Badge
                  variant="secondary"
                  className={`ml-2 text-xs ${
                    highlightedCategory === category.categoryId
                      ? "bg-white/20 text-white"
                      : "bg-gray-100 text-gray-600"
                  }`}
                >
                  {category.count}
                </Badge> */}
              </Button>
            ))}
        </div>

        {/* Tablet Categories - Grid */}
        <div className="hidden md:grid lg:hidden grid-cols-3 gap-2">
          {categories
            .filter((category) => category.count > 0)
            .map((category, index) => (
              <Button
                key={category.categoryId}
                ref={(el) => (categoryRefs.current[category.categoryId] = el)}
                variant={
                  highlightedCategory === category.categoryId
                    ? "default"
                    : "outline"
                }
                onClick={() => onCategorySelect(category.categoryId)}
                className={`h-12 px-4 transition-all duration-200 ${
                  highlightedCategory === category.categoryId
                    ? "bg-emerald-600 hover:bg-emerald-700 text-white shadow-lg"
                    : "hover:bg-emerald-50 hover:border-emerald-300 hover:text-emerald-700 hover:shadow-md"
                }`}
              >
                {/* <span className="mr-2">{category.emoji}</span> */}
                <span className="font-medium text-sm">{category.name}</span>
                <Badge
                  variant="secondary"
                  className={`ml-2 text-xs ${
                    highlightedCategory === category.categoryId
                      ? "bg-white/20 text-white"
                      : "bg-gray-100 text-gray-600"
                  }`}
                >
                  {category.count}
                </Badge>
              </Button>
            ))}
        </div>

        {/* Mobile Categories - Enhanced Horizontal Scroll */}
        <div className="md:hidden relative">
          {/* Scroll Buttons */}
          <div className="absolute left-0 top-1/2 -translate-y-1/2 z-10">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => scrollCategories("left")}
              className="h-8 w-8 p-0 bg-white/80 backdrop-blur-sm shadow-md hover:bg-white"
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
          </div>

          <div className="absolute right-0 top-1/2 -translate-y-1/2 z-10">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => scrollCategories("right")}
              className="h-8 w-8 p-0 bg-white/80 backdrop-blur-sm shadow-md hover:bg-white"
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>

          {/* Scrollable Categories */}
          <div
            id="categories-scroll"
            className="flex space-x-3 overflow-x-auto pb-4 scrollbar-hide px-8"
            onScroll={(e) => setScrollPosition(e.currentTarget.scrollLeft)}
          >
            {categories
              .filter((category) => category.count > 0)
              .map((category, index) => (
                <Button
                  key={category.categoryId}
                  ref={(el) => (categoryRefs.current[category.categoryId] = el)}
                  variant={
                    highlightedCategory === category.categoryId
                      ? "default"
                      : "outline"
                  }
                  onClick={() => onCategorySelect(category.categoryId)}
                  className={`h-12 px-4 whitespace-nowrap flex-shrink-0 transition-all duration-200 min-w-[120px] ${
                    highlightedCategory === category.categoryId
                      ? "bg-emerald-600 hover:bg-emerald-700 text-white shadow-lg scale-105"
                      : "hover:bg-emerald-50 hover:border-emerald-300 hover:text-emerald-700 hover:shadow-md hover:scale-105"
                  }`}
                >
                  <div className="flex flex-col items-start">
                    <span className="text-sm font-medium leading-tight">
                      {category.name}
                    </span>
                    {/* <Badge
                      variant="secondary"
                      className={`text-xs mt-0.5 ${
                        highlightedCategory === category.categoryId
                          ? "bg-white/20 text-white"
                          : "bg-gray-100 text-gray-600"
                      }`}
                    >
                      {category.count} items
                    </Badge> */}
                  </div>
                </Button>
              ))}
          </div>

          {/* Scroll Indicator */}
          <div className="flex justify-center mt-2">
            <div className="flex space-x-1">
              {Array.from({ length: Math.ceil(categories.length / 3) }).map(
                (_, index) => (
                  <div
                    key={index}
                    className={`w-2 h-2 rounded-full transition-all duration-200 ${
                      Math.floor(scrollPosition / 200) === index
                        ? "bg-emerald-600"
                        : "bg-gray-300"
                    }`}
                  />
                ),
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="max-w-7xl mx-auto mobile-padding mt-2">
        {/* <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" /> */}
        <Input
          placeholder="Search for dishes, ingredients, or flavors..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-12 h-12 sm:h-12 border-gray-200 focus:border-emerald-500 focus:ring-emerald-500 bg-white shadow-sm hover:shadow-md transition-all duration-200 text-base"
        />
        {searchQuery && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setSearchQuery("")}
            className="absolute right-2 top-1/2 transform -translate-y-1/2 h-8 w-8 p-0 text-gray-400 hover:text-gray-600"
          >
            ×
          </Button>
        )}
      </div>
    </section>
  );
}
